import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { workIdService, WorkIdRecord, WorkIdQueryParams, WorkIdFilterOptions, WorkIdListFilters } from '../workIdService';
import WorkIdActionsMenu from './WorkIdActionsMenu';
import { useWorkIdActionItems } from '../hooks/useWorkIdActionItems';
import { usePermissions } from '../../../hooks/usePermissions';
import { Alert, Spinner, Button, Table } from '@/components/ui';

const GOOGLE_MAPS_LIBRARIES: ('places' | 'geometry')[] = ['places', 'geometry'];

/** Stable color palette for foreman areas (matches reference design). */
const FOREMAN_AREA_COLORS = [
  '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231', '#911eb4',
  '#42d4f4', '#f032e6', '#bfef45', '#fabed4', '#469990', '#dcbeff',
  '#9a6324', '#fffac8', '#800000', '#aaffc3', '#808000', '#ffd8b1',
  '#000075', '#808080'
];

const DEFAULT_CENTER = { lat: 54.5, lng: -124.5 }; // British Columbia center
const DEFAULT_ZOOM = 5;

interface WorkIdsMapViewProps {
  refreshKey: number;
  filters: WorkIdListFilters;
  search?: string;
  filterOptions: WorkIdFilterOptions;
  onApprove?: (record: WorkIdRecord) => void;
  onCancel?: (record: WorkIdRecord) => void;
  onCloseWithoutAction?: (record: WorkIdRecord) => void;
  onReopen?: (record: WorkIdRecord) => void;
  onAssignExisting?: (record: WorkIdRecord) => void;
  onCreateNewTask?: (record: WorkIdRecord) => void;
  onUnassign?: (record: WorkIdRecord) => void;
  onMerge?: (record: WorkIdRecord) => void;
  onViewMergeSuggestions?: (record: WorkIdRecord) => void;
  onUnmerge?: (record: WorkIdRecord) => void;
  onReviewHistory?: (record: WorkIdRecord) => void;
  onEdit?: (record: WorkIdRecord) => void;
  onReopenTask?: (record: WorkIdRecord) => void;
  onReportAccomplishment?: (record: WorkIdRecord) => void;
  onCloseTask?: (record: WorkIdRecord) => void;
}

const formatReportedBy = (record: WorkIdRecord): string => {
  if (!record.reportedBy) return '-';
  const { name, lastName } = record.reportedBy;
  return [name, lastName].filter(Boolean).join(' ') || '-';
};

const formatDate = (dateStr: string): string => {
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? dateStr
      : d.toLocaleString(undefined, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
  } catch {
    return dateStr;
  }
};

const formatAsset = (record: WorkIdRecord): string => {
  if (!record.asset) return '-';
  const name = record.asset.name || '';
  const num = record.asset.assetNumber;
  return num ? `${name} #${num}` : name || '-';
};

const POPUP_W = 320;
const POPUP_MAX_H = 400;
const POPUP_PAD = 12;

/**
 * Use OverlayView projection to get accurate marker position in container pixels.
 */
function useMarkerPixelPosition(
  map: google.maps.Map | null,
  lat: number | undefined,
  lng: number | undefined,
  boundsVersion: number
): { markerPx: number; markerPy: number } | null {
  const [pos, setPos] = useState<{ markerPx: number; markerPy: number } | null>(null);

  useEffect(() => {
    if (!map || lat == null || lng == null) {
      setPos(null);
      return;
    }
    const latLng = new google.maps.LatLng(lat, lng);
    const overlay = new (class extends google.maps.OverlayView {
      onAdd() {}
      draw() {
        const proj = this.getProjection();
        if (!proj) return;
        const pixel = proj.fromLatLngToContainerPixel(latLng);
        if (pixel) setPos({ markerPx: pixel.x, markerPy: pixel.y });
      }
      onRemove() {}
    })();
    overlay.setMap(map);
    return () => {
      overlay.setMap(null);
      setPos(null);
    };
  }, [map, lat, lng, boundsVersion]);

  return pos;
}

interface MapMarkerProps {
  record: WorkIdRecord & { latitude: number; longitude: number };
  color: string;
  onHover: () => void;
  onHoverEnd: () => void;
  onClick: (e: google.maps.MapMouseEvent) => void;
}

const MapMarker: React.FC<MapMarkerProps> = ({ record, color, onHover, onHoverEnd, onClick }) => {
  return (
    <Marker
      position={{ lat: record.latitude, lng: record.longitude }}
      icon={{
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '#333',
        strokeWeight: 1
      }}
      onMouseOver={onHover}
      onMouseOut={onHoverEnd}
      onClick={onClick}
    />
  );
};

const WorkIdsMapView: React.FC<WorkIdsMapViewProps> = ({
  refreshKey,
  filters,
  search,
  filterOptions,
  onApprove,
  onCancel,
  onCloseWithoutAction,
  onReopen,
  onAssignExisting,
  onCreateNewTask,
  onUnassign,
  onMerge,
  onViewMergeSuggestions,
  onUnmerge,
  onReviewHistory,
  onEdit,
  onReopenTask,
  onReportAccomplishment,
  onCloseTask
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  const [workIds, setWorkIds] = useState<WorkIdRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [mapBoundsVersion, setMapBoundsVersion] = useState(0);
  const hoverShowTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverEndTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; record: WorkIdRecord } | null>(null);
  const [exportingId, setExportingId] = useState<number | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  const { hasPermission } = usePermissions();
  const hasWorkIdPerm = (key: string) => hasPermission(`workId:${key}`);
  const canExport = hasWorkIdPerm('view');

  const actionCallbacks = useMemo(
    () => ({
      onEdit,
      onApprove,
      onCancel,
      onCloseWithoutAction,
      onReopen,
      onReopenTask,
      onReportAccomplishment,
      onCloseTask,
      onAssignExisting,
      onCreateNewTask,
      onUnassign,
      onMerge,
      onViewMergeSuggestions,
      onUnmerge,
      onReviewHistory,
      onExportToPdf: canExport
        ? async (record: WorkIdRecord) => {
            try {
              setExportingId(record.id);
              const blob = await workIdService.exportToPdf(record.id);
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              const label = (record.workIdentificationId || `WorkID_${record.id}`).replace(/[/\\?*:<>|"\s]/g, '_');
              a.download = `WorkID_${label}_${new Date().toISOString().split('T')[0]}.pdf`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            } catch (err) {
              console.error('Export to PDF failed:', err);
              alert(err instanceof Error ? err.message : 'Failed to export PDF');
            } finally {
              setExportingId(null);
            }
          }
        : undefined
    }),
    [
      onEdit,
      onApprove,
      onCancel,
      onCloseWithoutAction,
      onReopen,
      onReopenTask,
      onReportAccomplishment,
      onCloseTask,
      onAssignExisting,
      onCreateNewTask,
      onUnassign,
      onMerge,
      onViewMergeSuggestions,
      onUnmerge,
      onReviewHistory,
      canExport
    ]
  );

  const foremanAreaColorMap = useMemo(() => {
    const areas = [...(filterOptions.foremanAreas || [])].sort((a, b) => a.name.localeCompare(b.name));
    const map = new Map<number | string, string>();
    map.set(0, '#9ca3af'); // (Blank)
    areas.forEach((area, idx) => {
      map.set(area.id, FOREMAN_AREA_COLORS[idx % FOREMAN_AREA_COLORS.length]);
    });
    return map;
  }, [filterOptions.foremanAreas]);

  const getMarkerColor = useCallback(
    (record: WorkIdRecord): string => {
      const areaId = record.foremanAreaId ?? 0;
      const areaName = record.foremanArea?.name;
      if (areaName) {
        const area = filterOptions.foremanAreas?.find(a => a.name === areaName);
        if (area) return foremanAreaColorMap.get(area.id) ?? FOREMAN_AREA_COLORS[0];
      }
      return foremanAreaColorMap.get(areaId) ?? foremanAreaColorMap.get(0) ?? FOREMAN_AREA_COLORS[0];
    },
    [foremanAreaColorMap, filterOptions.foremanAreas]
  );

  useEffect(() => {
    const controller = new AbortController();
    let aborted = false;

    const fetchWorkIds = async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Omit<WorkIdQueryParams, 'page' | 'limit'> = {
          ...(filters.status && filters.status !== 'exclude-cancelled' && { status: filters.status }),
          ...(filters.foremanAreaId && { foremanAreaId: filters.foremanAreaId }),
          ...(filters.serviceAreaId && { serviceAreaId: filters.serviceAreaId }),
          ...(filters.assetType && { assetType: filters.assetType }),
          ...(search && search.trim() && { search: search.trim() }),
          ...(filters.priority && { priority: filters.priority }),
          ...(filters.reportedById && { reportedById: filters.reportedById }),
          ...(filters.workSource && { workSource: filters.workSource }),
          ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
          ...(filters.dateTo && { dateTo: filters.dateTo }),
          ...((filters.dateFilterType === 'taskCompletion' || filters.dateFilterType === 'reportedOn') && {
            dateFilterType: filters.dateFilterType
          })
        };
        const { data } = await workIdService.getWorkIdsForMap(params, controller.signal);
        if (!controller.signal.aborted) {
          setWorkIds(data.filter((w) => w.latitude != null && w.longitude != null));
        }
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError('Failed to load work IDs for map.');
          setWorkIds([]);
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    };

    fetchWorkIds();
    return () => {
      aborted = true;
      controller.abort();
    };
  }, [
    refreshKey,
    filters.serviceAreaId,
    filters.foremanAreaId,
    filters.assetType,
    filters.status,
    filters.priority,
    filters.reportedById,
    filters.workSource,
    filters.dateFrom,
    filters.dateTo,
    filters.dateFilterType,
    search
  ]);

  useEffect(
    () => () => {
      if (hoverShowTimeoutRef.current) clearTimeout(hoverShowTimeoutRef.current);
      if (hoverEndTimeoutRef.current) clearTimeout(hoverEndTimeoutRef.current);
    },
    []
  );

  const markersWithCoords = useMemo(
    () => workIds.filter((w) => w.latitude != null && w.longitude != null) as (WorkIdRecord & { latitude: number; longitude: number })[],
    [workIds]
  );

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapInstance(map);
  }, []);

  const hoveredRecord = hoveredId ? markersWithCoords.find((w) => w.id === hoveredId) : null;
  const markerPos = useMarkerPixelPosition(
    mapInstance,
    hoveredRecord?.latitude,
    hoveredRecord?.longitude,
    mapBoundsVersion
  );

  const bounds = useMemo(() => {
    if (markersWithCoords.length === 0) return null;
    const b = new google.maps.LatLngBounds();
    markersWithCoords.forEach((w) => {
      b.extend({ lat: w.latitude!, lng: w.longitude! });
    });
    return b;
  }, [markersWithCoords]);

  useEffect(() => {
    if (bounds && mapRef.current && markersWithCoords.length > 0) {
      mapRef.current.fitBounds(bounds, 50);
    }
  }, [bounds, markersWithCoords.length]);

  const foremanAreasForLegend = useMemo(() => {
    const seen = new Map<string | number, { name: string; color: string }>();
    markersWithCoords.forEach((w) => {
      const key = w.foremanAreaId ?? 0;
      const label = w.foremanArea?.name || '(Blank)';
      if (!seen.has(key)) {
        seen.set(key, { name: label, color: getMarkerColor(w) });
      }
    });
    return Array.from(seen.entries())
      .map(([, v]) => v)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [markersWithCoords, getMarkerColor]);

  if (loadError) {
    return (
      <Alert variant="warning">
        Google Maps failed to load. Ensure REACT_APP_GOOGLE_MAPS_API_KEY is set.
      </Alert>
    );
  }

  if (!isLoaded) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-p-5">
        <Spinner animation="border" role="status">Loading map…</Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="work-ids-map-view tw-flex tw-flex-col tw-grow min-h-0" style={{ minHeight: 0 }}>
      <h4 className="tw-mb-2 tw-shrink-0">Map of Works Identified</h4>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-2 tw-shrink-0">
        <span className="tw-text-content-muted tw-text-sm">Foreman Area:</span>
        {foremanAreasForLegend.map((fa) => (
          <span key={fa.name} className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm">
            <span
              className="tw-rounded-full tw-inline-block"
              style={{ width: 10, height: 10, backgroundColor: fa.color }}
              aria-hidden
            />
            <span className="tw-truncate" style={{ maxWidth: 90 }} title={fa.name}>
              {fa.name}
            </span>
          </span>
        ))}
      </div>
      <div className="tw-grow" style={{ minHeight: 400, position: 'relative' }}>
        {loading && (
          <div
            className="tw-absolute tw-top-1/2 tw-left-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-bg-surface tw-rounded tw-shadow tw-p-2"
            style={{ zIndex: 10 }}
          >
            <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
          </div>
        )}
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          onLoad={onMapLoad}
          onIdle={() => setMapBoundsVersion((v) => v + 1)}
          options={{
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true
          }}
        >
          {markersWithCoords.map((record) => (
            <MapMarker
              key={record.id}
              record={record}
              color={getMarkerColor(record)}
              onHover={() => {
                if (hoverEndTimeoutRef.current) {
                  clearTimeout(hoverEndTimeoutRef.current);
                  hoverEndTimeoutRef.current = null;
                }
                if (hoverShowTimeoutRef.current) {
                  clearTimeout(hoverShowTimeoutRef.current);
                }
                hoverShowTimeoutRef.current = setTimeout(() => setHoveredId(record.id), 120);
              }}
              onHoverEnd={() => {
                if (hoverShowTimeoutRef.current) {
                  clearTimeout(hoverShowTimeoutRef.current);
                  hoverShowTimeoutRef.current = null;
                }
                // Keep info window visible while context menu is open
                if (!contextMenu) {
                  hoverEndTimeoutRef.current = setTimeout(() => setHoveredId(null), 400);
                }
              }}
              onClick={(e) => {
                if (hoverShowTimeoutRef.current) {
                  clearTimeout(hoverShowTimeoutRef.current);
                  hoverShowTimeoutRef.current = null;
                }
                if (hoverEndTimeoutRef.current) {
                  clearTimeout(hoverEndTimeoutRef.current);
                  hoverEndTimeoutRef.current = null;
                }
                // Keep info visible when opening actions menu
                setHoveredId(record.id);
                const dom = e.domEvent as MouseEvent | undefined;
                if (dom) {
                  setContextMenu({ x: dom.clientX, y: dom.clientY, record });
                }
              }}
            />
          ))}
        </GoogleMap>
        {hoveredId && hoveredRecord && (() => {
          const record = hoveredRecord;
          const map = mapRef.current;
          const mapDiv = map?.getDiv();
          if (!mapDiv || !markerPos) return null;
          const mapW = mapDiv.offsetWidth || 400;
          const mapH = mapDiv.offsetHeight || 500;
          const { markerPx, markerPy } = markerPos;

          // Place box to whichever side has more room, vertically centred on marker
          const b = map?.getBounds();
          const preferRight = !b || record.longitude < (b.getNorthEast().lng() + b.getSouthWest().lng()) / 2;
          const GAP = 14; // gap between marker edge and popup box
          let left = preferRight ? markerPx + GAP : markerPx - POPUP_W - GAP;
          left = Math.max(POPUP_PAD, Math.min(mapW - POPUP_W - POPUP_PAD, left));
          let top = markerPy - POPUP_MAX_H / 2;
          top = Math.max(POPUP_PAD, Math.min(mapH - POPUP_MAX_H - POPUP_PAD, top));

          const popup = (
            <div
              className="tw-absolute tw-bg-surface tw-rounded tw-shadow tw-border tw-border-line tw-text-sm"
              style={{ left, top, width: POPUP_W, maxHeight: POPUP_MAX_H, overflowY: 'auto', zIndex: 1000 }}
              onMouseEnter={() => {
                if (hoverEndTimeoutRef.current) {
                  clearTimeout(hoverEndTimeoutRef.current);
                  hoverEndTimeoutRef.current = null;
                }
              }}
              onMouseLeave={() => {
                if (!contextMenu) {
                  hoverEndTimeoutRef.current = setTimeout(() => setHoveredId(null), 400);
                }
              }}
            >
              <div className="tw-absolute tw-top-0 tw-right-0 tw-p-1">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="tw-text-secondary tw-p-0"
                  style={{ fontSize: '1.25rem', lineHeight: 1 }}
                  onClick={() => setHoveredId(null)}
                  aria-label="Close"
                >×</Button>
              </div>
              <div
                className="tw-bg-surface-alt tw-border-b tw-border-line tw-px-2 tw-py-1 tw-font-semibold"
                style={{ fontSize: '1rem', marginBottom: 8, lineHeight: 1.4 }}
              >
                Work ID: {record.workIdentificationId || record.id}
              </div>
              <Table size="sm" borderless className="tw-mb-0 tw-text-sm">
                <tbody>
                  <tr><td className="tw-text-content-muted tw-pr-2">Foreman Area</td><td>{record.foremanArea?.name || '-'}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2">Latitude</td><td>{record.latitude}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2">Longitude</td><td>{record.longitude}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2">Inspector Name</td><td>{formatReportedBy(record)}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2">Date Reported</td><td>{formatDate(record.reportedOn)}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2">Highway Unique</td><td>{record.asset?.highwayUnique || '-'}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2">Road Name</td><td>{formatAsset(record)}</td></tr>
                  <tr><td className="tw-text-content-muted tw-pr-2 tw-align-top">Comment</td><td style={{ wordBreak: 'break-word' }}>{record.comments || '-'}</td></tr>
                </tbody>
              </Table>
            </div>
          );
          return ReactDOM.createPortal(popup, mapDiv);
        })()}
      </div>
      <div className="tw-text-content-muted tw-text-sm tw-mt-2 tw-shrink-0">
        Showing {markersWithCoords.length} work ID{markersWithCoords.length !== 1 ? 's' : ''} with coordinates.
        {workIds.length > markersWithCoords.length && ` (${workIds.length - markersWithCoords.length} without coordinates hidden)`}
      </div>

      {contextMenu && (
        <MapMarkerContextMenu
          record={contextMenu.record}
          x={contextMenu.x}
          y={contextMenu.y}
          actionCallbacks={actionCallbacks}
          exportingId={exportingId}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};

interface MapMarkerContextMenuProps {
  record: WorkIdRecord;
  x: number;
  y: number;
  actionCallbacks: Parameters<typeof useWorkIdActionItems>[1];
  exportingId: number | null;
  onClose: () => void;
}

const MapMarkerContextMenu: React.FC<MapMarkerContextMenuProps> = ({
  record,
  x,
  y,
  actionCallbacks,
  exportingId,
  onClose
}) => {
  const items = useWorkIdActionItems(record, actionCallbacks, exportingId);
  if (items.length === 0) return null;
  return (
    <WorkIdActionsMenu left={x} top={y} onClose={onClose} items={items} />
  );
};

export default WorkIdsMapView;
